import * as React from 'react'

import Map, {
  Bounds,
  CalloutView,
  IMarker,
  MarkerPriority,
  PinType,
  SetBounds,
  SetBoundsType,
} from 'main/components/common/Map'
import {
  height as viewFormHeight,
  margin as viewFormMargin,
} from 'main/components/common/SplitMapView/ViewControl'
import { LazyTargetType as CalloutLazyTargetType, MapCallout } from 'main/components/school/Callout'
import { useWhitelabel } from 'main/framework'
import { SchoolListingType } from 'main/gql/globalTypes'
import { NetworkMarkers, retrieveMarkers } from 'main/util/asset'
import { UnreachableCaseError } from 'main/util/ts'
import { useTranslation } from 'react-i18next'
import { LOCIZE_NAMESPACE } from '../../../../common'

import { SearchPage_result } from '../../gql'
import { i18nKeyPrefix } from '../../i18n'
import { Container } from './MapSection.styles'

const PRE = `${i18nKeyPrefix}-MAP-SECTION`

interface IProps {
  bounds: Bounds | null
  mobile: boolean
  result: SearchPage_result | null
  onChangeBounds: (bounds: Bounds | null) => void
}

type Selection =
  | { id: string; type: SchoolListingType.INTERNAL }
  | { id: string; type: SchoolListingType.EXTERNAL }

export default function MapSection(props: IProps) {
  const { bounds, mobile, result, onChangeBounds } = props
  const [selection, setSelection] = React.useState<Selection | null>(null)
  const whitelabel = useWhitelabel()
  const { t } = useTranslation(LOCIZE_NAMESPACE)
  const location = result && result.location

  return (
    <Container>
      {result && (
        <Map
          bounds={bounds}
          calloutView={mobile ? CalloutView.Fixed : CalloutView.Relative}
          fixedCalloutPadding={{ bottom: viewFormHeight + viewFormMargin }}
          markers={buildMarkers(whitelabel.isWhitelabeled)}
          setBounds={setBounds()}
          onClick={handleClick}
          location={location}
        />
      )}
    </Container>
  )

  function handleClick() {
    setSelection(null)
  }

  function buildMarkers(isWhiteLabeled: boolean) {
    const networkMarkerAssets: NetworkMarkers | undefined = retrieveMarkers(whitelabel)

    const markers: IMarker[] = []
    if (!result) return markers
    if (!result.allCoordinates) return markers
    if (result.listings.length === 0) return markers

    result.allCoordinates.forEach(coordinates => {
      if (!coordinates) return
      if (coordinates.length !== 4) return

      const [schoolId, externalSchoolId, latitude, longitude] = coordinates
      if (schoolId === null && externalSchoolId === null) return
      if (latitude === null) return
      if (longitude === null) return

      const existsOnListing = result.listings.some(listing => {
        const coordinatesSchoolId = schoolId ? Number(schoolId) : null
        const coordinatesExternalSchoolId = externalSchoolId ? Number(externalSchoolId) : null

        return (
          listing.externalSchoolId === coordinatesExternalSchoolId &&
          listing.schoolId === coordinatesSchoolId
        )
      })

      if (!existsOnListing && isWhiteLabeled) return

      markers.push({
        callout: <MapCallout lazyTarget={getCalloutTarget(schoolId, externalSchoolId)} />,
        coordinates: {
          lat: parseFloat(latitude),
          lng: parseFloat(longitude),
        },
        onClick: () => handleMarkerClick(schoolId, externalSchoolId),
        pin:
          schoolId === null
            ? { _type: PinType.Generic, src: networkMarkerAssets }
            : { _type: PinType.Wonderschool, src: networkMarkerAssets },
        priority: schoolId === null ? MarkerPriority.DEFAULT : MarkerPriority.HIGH,
        selected: isListingSelected(schoolId, externalSchoolId),
      })
    })

    return markers
  }

  function handleMarkerClick(schoolId: string | null, externalSchoolId: string | null) {
    if (schoolId !== null) {
      setSelection({ id: schoolId, type: SchoolListingType.INTERNAL })
    }

    if (externalSchoolId !== null) {
      setSelection({ id: externalSchoolId, type: SchoolListingType.EXTERNAL })
    }
  }

  function isListingSelected(schoolId: string | null, externalSchoolId: string | null) {
    if (!selection) return false

    switch (selection.type) {
      case SchoolListingType.INTERNAL:
        return schoolId !== null && schoolId === selection.id
      case SchoolListingType.EXTERNAL:
        return externalSchoolId !== null && externalSchoolId === selection.id
      default:
        throw new UnreachableCaseError(selection)
    }
  }

  function setBounds(): SetBounds {
    return {
      __typename: SetBoundsType.OnRequest,
      label: t(`${PRE}-LABEL`),
      onChangeBounds,
    }
  }
}

function getCalloutTarget(schoolId: string | null, externalSchoolId: string | null) {
  if (schoolId !== null) {
    return {
      _type: CalloutLazyTargetType.SchoolListing,
      id: parseInt(schoolId, 10),
      type: SchoolListingType.INTERNAL,
    }
  }

  if (externalSchoolId !== null) {
    return {
      _type: CalloutLazyTargetType.SchoolListing,
      id: parseInt(externalSchoolId, 10),
      type: SchoolListingType.EXTERNAL,
    }
  }

  throw new Error('missing school and external school id')
}
